import { renderRichText } from '@storyblok/react';
import { Breadcrumbs } from '@/components/Shared/Breadcrumbs';
import { CTA } from '@/components/Shared/CTA/CallToAction';
import { FormattedText } from '@/components/Shared/FormattedText';
import { Container } from '@/components/Shared/Layout/Container';
import { isArrayWithValue } from '@/utils/helpers/isArrayWithValue';
import { IHeroBlok } from '@/utils/interfaces/bloks';
import { useEffect, useState } from 'react';

interface IProps {
  blok: IHeroBlok;
  isHome?: boolean;
}

export const HeroContentWithSearch: React.FC<IProps> = ({ blok, isHome }) => {
  const headingClasses = ['text-inherit mb-0'];
  if (isHome) {
    headingClasses.push('text-7xl lg:text-8xl leading-snug');
  }
  const words = ['House', 'Pool', 'ADU', 'Shed', 'Garage'];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % words.length);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  const renderBodyContent = () => {
    if (!blok.body) return null;

    if (typeof blok.body === 'string') {
      return (
        <div className="text-[20px] font-normal text-center">
          {blok.body}
        </div>
      );
    }

    try {
      return (
        <div
          className="text-[20px] font-normal text-center"
          dangerouslySetInnerHTML={{ __html: renderRichText(blok.body) }}
        />
      );
    } catch (error) {
      console.error('Error rendering richtext:', error);
      return null;
    }
  };

  return (
    <div className="flex items-center justify-between w-full">
      <Container>
        <div className=" lg:pt-[23px] pb-[7rem] w-full relative z-10">
          {!isHome && <Breadcrumbs className="mb-3" />}

          {isHome ? (
            <>
              <h1 className="text-3xl sm:text-5xl items-center md:items-end md:flex-row flex-col flex justify-center lg:text-7xl leading-snug md:mb-4">
                <span className="text-white text-center font-normal">
                  {blok.heading}&nbsp;
                </span>
                {
                  blok.style === 'home' &&
                  <div className="flex items-end">
                    <div className="text-green text-end font-normal h-[50px] overflow-hidden relative w-fit">
                      <div
                        className="transition-transform duration-700 ease-in-out"
                        style={{ transform: `translateY(-${index * 50}px)` }}
                      >
                        {words.map((word, i) => (
                          <div
                            key={i}
                            className="h-[50px] items-end text-7xl font-medium justify-center md:justify-start flex"
                          >
                            {word}?
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                }
              </h1>
              {renderBodyContent()}
              <div className='flex flex-col justify-center items-center'>
                <p className='text-sm font-normal flex mt-4 items-center justify-center gap-2'>
                  <span>{blok.disclaimerText}</span>
                </p>
                {isArrayWithValue(blok.cta) && (
                  <div className="flex flex-wrap gap-2">
                    {blok.cta.map((cta, key) => (
                      <div className="inline-flex learn-more-cta " key={key}>
                        <CTA blok={cta} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {blok.heading && (
                <FormattedText
                  tag="h1"
                  text={blok.heading}
                  className={headingClasses.join(' ')}
                />
              )}
              {renderBodyContent()}
              {isArrayWithValue(blok.cta) && (
                <div className="mt-6 lg:mt-10">
                  <div className="flex flex-wrap gap-2">
                    {blok.cta.map((cta, key) => (
                      <div className="inline-flex" key={key}>
                        <CTA blok={cta} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Container>
    </div>
  );
};